// Fixture registry — the extensibility seam for shop content.
//
// Every fixture (longbox, wall shelf, vinyl bin, future ?) is a self-registering
// module. Stage walks the registry to harvest anchors and run builders.
//
// The marker contract (named Empty in the GLB with position/rotation/scale)
// is fixed. The builder is open: it can spawn any geometry, attach any
// interaction, drive any state machine. New product interactions don't need
// core changes — they just register a new entry.
//
// See documentation/SHOP_CONTRACT.md for the author-facing contract.

const _registry = [];

// Register a fixture type. Called at module load by each fixture file.
//
// Required:
//   kind:                 short stable identifier (e.g. "vinyl-bin")
//   pattern:              RegExp matching the anchor empty's name; the first
//                         capture group (if any) is exposed as anchor.index
//   acceptsProductTypes:  array of product `type` strings the builder consumes
//   build(ctx):           async fn called once per matched anchor
//
// Optional:
//   describe():           returns metadata about the fixture's interaction
//                         model (used by validation / tooling / accessibility)
export function registerFixture(entry) {
	if (!entry || !entry.kind || !entry.pattern || typeof entry.build !== "function") {
		throw new Error("[fixture-registry] invalid registration: " + JSON.stringify(entry?.kind));
	}
	if (!Array.isArray(entry.acceptsProductTypes)) {
		throw new Error(`[fixture-registry] '${entry.kind}' must declare acceptsProductTypes (array)`);
	}
	_registry.push(entry);
}

// All registered fixture entries.
export function listFixtures() {
	return _registry.slice();
}

// Look up the fixture entry whose pattern matches a given empty name.
// Returns { entry, match } or null.
export function matchFixture(name) {
	for (const entry of _registry) {
		const m = name.match(entry.pattern);
		if (m) return { entry, match: m };
	}
	return null;
}

// Group anchors harvested from a shell GLB by fixture kind. Returns a Map
// keyed by fixture kind, value = sorted array of anchor entries.
//
// anchor entry shape: { name, index, position, scale, rotationY, fixtureKind }
export function groupAnchorsByKind(anchors) {
	const grouped = new Map();
	for (const a of anchors) {
		if (!grouped.has(a.fixtureKind)) grouped.set(a.fixtureKind, []);
		grouped.get(a.fixtureKind).push(a);
	}
	for (const arr of grouped.values()) {
		arr.sort((p, q) => (p.index || 0) - (q.index || 0));
	}
	return grouped;
}
